import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import { api, money } from "../../lib/api";

const BLANK = {
  title: "", slug: "", description: "", category: "bags", price_cents: 0, quote_only: false,
  price_is_placeholder: false, image: "", colors: "Pastel mix, Pick my colors", materials: "",
  measurements: "", care: "", story: "", stock: 0, badge: "", one_of_a_kind: false, featured: true, active: true,
};

export default function Products() {
  const [products, setProducts] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api("/admin/products").then(setProducts).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  const save = async (p) => {
    const body = { ...p, colors: typeof p.colors === "string" ? p.colors.split(",").map((s) => s.trim()).filter(Boolean) : p.colors };
    try {
      if (p.id) await api(`/admin/products/${p.id}`, { method: "PATCH", body });
      else await api("/admin/products", { method: "POST", body });
      toast("Product saved");
      setEdit(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!products) return <p className="text-muted font-bold">Winding the yarn…</p>;

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-semibold text-2xl">Products</h1>
        <button data-testid="admin-product-new" onClick={() => setEdit(BLANK)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-candy text-white font-bold text-sm hover:bg-candy-deep transition-colors duration-200">
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>
      <div className="mt-6 space-y-3">
        {products.map((p) => (
          <div key={p.id} className="bg-white rounded-3xl border border-candy-soft/70 p-4 flex items-center gap-4" data-testid={`admin-product-${p.slug}`}>
            <img src={p.image} alt="" className="w-14 h-14 rounded-2xl object-cover bg-candy-soft" />
            <div className="min-w-0">
              <p className="font-display font-semibold">{p.title}</p>
              <p className="text-xs font-bold text-muted">
                {p.quote_only ? "Quote-only — asks for price" : `${money(p.price_cents)}${p.price_is_placeholder ? " (placeholder)" : ""}`} · {p.stock} in stock · {p.category}
                {!p.active && " · hidden"}
              </p>
            </div>
            <button data-testid={`admin-product-edit-${p.slug}`} onClick={() => setEdit({ ...p, colors: (p.colors || []).join(", ") })} className="ml-auto p-2.5 rounded-full hover:bg-candy-soft transition-colors duration-200" aria-label={`Edit ${p.title}`}>
              <Pencil className="w-4 h-4 text-candy-deep" />
            </button>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-cloud shadow-card p-6 w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-product-editor">
            <h2 className="font-display font-semibold text-xl">{edit.id ? "Edit" : "New"} product</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[
                { k: "title", label: "Title" }, { k: "slug", label: "Slug" }, { k: "image", label: "Image URL (or upload in Media)" },
                { k: "materials", label: "Materials" }, { k: "measurements", label: "Measurements" }, { k: "care", label: "Care" }, { k: "story", label: "Handmade story" }, { k: "badge", label: "Badge (e.g. New)" },
              ].map((f) => (
                <label key={f.k} className={["materials", "measurements", "care", "story"].includes(f.k) ? "col-span-2" : ""}>
                  <span className="text-xs font-bold text-ink/70">{f.label}</span>
                  <input data-testid={`admin-product-field-${f.k}`} value={edit[f.k] || ""} onChange={(e) => setEdit({ ...edit, [f.k]: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
                </label>
              ))}
              <label className="col-span-2">
                <span className="text-xs font-bold text-ink/70">Description</span>
                <textarea data-testid="admin-product-field-description" rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold resize-none" />
              </label>
              <label>
                <span className="text-xs font-bold text-ink/70">Category</span>
                <select data-testid="admin-product-field-category" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold">
                  {["bags", "plushies", "wearables"].map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </label>
              <label>
                <span className="text-xs font-bold text-ink/70">Price (cents)</span>
                <input data-testid="admin-product-field-price" type="number" min={0} value={edit.price_cents} onChange={(e) => setEdit({ ...edit, price_cents: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
              </label>
              <label>
                <span className="text-xs font-bold text-ink/70">Stock</span>
                <input data-testid="admin-product-field-stock" type="number" min={0} value={edit.stock} onChange={(e) => setEdit({ ...edit, stock: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
              </label>
              <label>
                <span className="text-xs font-bold text-ink/70">Colors (comma separated)</span>
                <input data-testid="admin-product-field-colors" value={edit.colors} onChange={(e) => setEdit({ ...edit, colors: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl bg-candy-mist border border-candy-soft text-sm font-semibold" />
              </label>
              {[
                { k: "quote_only", label: "Quote-only (Ask for price)" },
                { k: "price_is_placeholder", label: "Price is a placeholder" },
                { k: "one_of_a_kind", label: "One of a kind" },
                { k: "featured", label: "Featured on homepage" },
                { k: "active", label: "Active (visible in shop)" },
              ].map((f) => (
                <label key={f.k} className="col-span-2 flex items-center gap-3 bg-candy-mist rounded-xl px-3 py-2.5">
                  <input data-testid={`admin-product-field-${f.k}`} type="checkbox" checked={!!edit[f.k]} onChange={(e) => setEdit({ ...edit, [f.k]: e.target.checked })} className="w-4 h-4 accent-pink-500" />
                  <span className="text-sm font-bold">{f.label}</span>
                </label>
              ))}
            </div>
            <div className="mt-5 flex gap-3">
              <button data-testid="admin-product-save" onClick={() => save(edit)} className="flex-1 py-3 rounded-full bg-candy text-white font-bold hover:bg-candy-deep transition-colors duration-200">Save</button>
              <button data-testid="admin-product-cancel" onClick={() => setEdit(null)} className="flex-1 py-3 rounded-full border-2 border-candy-soft font-bold hover:bg-candy-soft transition-colors duration-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
